// Real case study data (from Monica Lockett's portfolio, adapted for ICG)

export const projects = [
  {
    slug: "comcast-doc-center",
    title: "Doc Center — Redesign",
    client: "Comcast",
    year: 2024,
    category: "Enterprise",
    tagline: "Cutting employee search time by 40%.",
    cover:
      "https://cdn.myportfolio.com/4c8b47e2c3929d5bf3d8de298f3d0e72/b0f8ccc4-b57a-4511-aacf-2e2b35a79050_car_16x9.png?h=c9710c98b6ac0573acce35860a9fcb15",
    color: "#111111",
    role: "Lead UX / Product Designer",
    team: ["Product Manager", "Engineering Lead", "Research Partner"],
    duration: "6 months",
    problem:
      "Comcast's internal document management center had grown into a sprawling maze. Employees were spending too much time hunting for files, and inconsistent patterns across the tool were quietly draining productivity every single day.",
    approach:
      "We started with the people actually using it — interviewing employees across departments, running task flows, and mapping every friction point. Then we rebuilt the information architecture, streamlined search, and introduced a cleaner, more predictable UI system.",
    outcome:
      "The redesigned Doc Center reduced average search time by roughly 40%, cut support tickets, and became a reference point for other internal tool redesigns at Comcast.",
    highlights: [
      "40% reduction in average search time",
      "Unified IA across 12+ document types",
      "Adopted as the new internal design standard",
    ],
    tags: ["UX Research", "Enterprise UX", "Design System"],
  },
  {
    slug: "att-partner-exchange",
    title: "Partner Exchange",
    client: "AT&T",
    year: 2017,
    category: "Enterprise",
    tagline: "Five disconnected tools, one unified partner experience.",
    cover:
      "https://cdn.myportfolio.com/4c8b47e2c3929d5bf3d8de298f3d0e72/07df1c27-831e-4cb2-b983-c589be0bb060_car_16x9.png?h=318665872ce8c3806ca17d14b02297c6",
    color: "#00A8E0",
    role: "Senior UX Designer",
    team: ["Product Lead", "3 Engineers", "Business Stakeholders"],
    duration: "9 months",
    problem:
      "AT&T's partners were jumping between five separate systems to manage accounts, orders, and customer relationships. The context switching was slowing everyone down and creating errors that were expensive to unwind.",
    approach:
      "We mapped every partner journey end-to-end, identified overlapping workflows, and designed a single B2B platform that felt like one product rather than five. Along the way, we worked closely with partners themselves to validate every major decision.",
    outcome:
      "Partner Exchange consolidated the ecosystem into one interface that partners actually enjoyed using — and gave AT&T a stronger, more efficient B2B relationship layer.",
    highlights: [
      "5 legacy tools consolidated into 1",
      "Redesigned account & order management",
      "Validated with 20+ partner interviews",
    ],
    tags: ["B2B", "Enterprise UX", "Journey Mapping"],
  },
  {
    slug: "ups-symphony",
    title: "UPS Symphony Dashboard",
    client: "UPS",
    year: 2023,
    category: "Enterprise",
    tagline: "Logistics at a glance for enterprise supply chains.",
    cover:
      "https://monicalockett.com/attached_assets/Screenshot_20251215_142008_Figma_1765830065214.jpg",
    color: "#5F3418",
    role: "Senior Product Designer",
    team: ["Product Manager", "Data Team", "Enterprise Customers"],
    duration: "8 months",
    problem:
      "UPS Supply Chain Symphony was carrying an enormous amount of data — shipments, suppliers, exceptions — but enterprise customers were struggling to find the signal in the noise.",
    approach:
      "We rebuilt the dashboard around the moments that actually mattered: at-a-glance status, exception handling, and drill-down details when needed. Every widget had to earn its place on the screen.",
    outcome:
      "A calmer, faster, more decision-friendly dashboard that turned Symphony into a tool logistics teams open first thing in the morning — not the last.",
    highlights: [
      "Streamlined exception workflow",
      "Configurable widget-based layout",
      "Reduced training time for new users",
    ],
    tags: ["Dashboards", "Enterprise UX", "Data Viz"],
  },
  {
    slug: "state-farm-7xd",
    title: "State Farm 7XD Design System",
    client: "State Farm",
    year: 2017,
    category: "Enterprise",
    tagline: "One design system, thousands of agents, faster training.",
    cover:
      "https://cdn.myportfolio.com/4c8b47e2c3929d5bf3d8de298f3d0e72/0a81438b-3f07-4979-aaa9-417eb15b8930_car_16x9.png?h=fed94b356374524c566a6523351be766",
    color: "#C41E3A",
    role: "UX Designer",
    team: ["Design System Team", "Frontend Engineers", "Agent Ops"],
    duration: "7 months",
    problem:
      "State Farm's agent-facing tools had drifted apart visually and interactively over the years. New agents had to learn each tool's quirks separately, which slowed onboarding and eroded confidence.",
    approach:
      "We audited every UI pattern in use across the suite, consolidated them into a shared system, documented it thoroughly, and partnered with engineers to make adoption easy rather than optional.",
    outcome:
      "The updated design system reduced agent training time by about 30% and gave every tool in the ecosystem a consistent, trustworthy feel.",
    highlights: [
      "~30% reduction in agent training time",
      "Documented pattern library",
      "Adopted across multiple agent tools",
    ],
    tags: ["Design System", "Enterprise UX", "Documentation"],
  },
  {
    slug: "builders-first-source",
    title: "Builders First Source",
    client: "Builders First Source",
    year: 2018,
    category: "Enterprise",
    tagline: "Turning 5% digital orders into 45%.",
    cover:
      "https://cdn.myportfolio.com/4c8b47e2c3929d5bf3d8de298f3d0e72/877972e5-d134-412b-9cf4-9ec0f5100c94_car_16x9.PNG?h=7a1e4e17a11fdd4b7e1fc4985b32adc7",
    color: "#F5A623",
    role: "Lead UX Designer",
    team: ["Product", "Engineering", "Contractor Advisors"],
    duration: "10 months",
    problem:
      "The largest building materials supplier in the US had a mostly offline ordering habit — only about 5% of orders were coming through digital channels. Contractors weren't the problem; the tool was.",
    approach:
      "We spent time on job sites, watched contractors work, and designed an e-commerce experience that respected their reality — quick reordering, clear pricing, and mobile-first flows for people who wear gloves.",
    outcome:
      "Digital orders jumped from ~5% to ~45%, and contractors started recommending the platform to each other.",
    highlights: [
      "Digital order share: 5% → 45%",
      "Mobile-first ordering flows",
      "Field research with real contractors",
    ],
    tags: ["E-commerce", "Field Research", "Mobile"],
  },
  {
    slug: "circulus",
    title: "Circulus — Community App",
    client: "Circulus",
    year: 2017,
    category: "Mobile App",
    tagline: "A 4.5-star app that got neighbours actually meeting.",
    cover:
      "https://cdn.myportfolio.com/4c8b47e2c3929d5bf3d8de298f3d0e72/61a0e54c-ada0-46b6-9f80-baf33c639a9c_car_16x9.png?h=9c33e1064bc58803bcb2f029ebe43da7",
    color: "#8B4CFC",
    role: "Lead Product Designer",
    team: ["Founder", "iOS Dev", "Community Manager"],
    duration: "5 months",
    problem:
      "The team had a big idea — connect local communities around shared interests and events — but no clear design language and no evidence yet that people would actually show up.",
    approach:
      "We ran interviews with prospective users, prototyped fast, tested weekly, and built a mobile-first product that made joining a group feel as easy as saying hi.",
    outcome:
      "The launched app hit a 4.5-star rating, and 78% of new users made a meaningful local connection within their first month.",
    highlights: [
      "4.5-star launch rating",
      "78% new users → real connections in month 1",
      "Mobile-first from day one",
    ],
    tags: ["Mobile", "Consumer", "User Research"],
  },
];

export const getProject = (slug) => projects.find((p) => p.slug === slug);
